/* import __COLOCATED_TEMPLATE__ from './ticket-type-changed-by-admin.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import Component from '@glimmer/component';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import type TicketTypeChangedByAdmin from 'embercom/objects/inbox/renderable/ticket-type-changed-by-admin';
import LinkWithText from 'embercom/helpers/link-with-text';
import type Session from 'embercom/services/session';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Conversation from 'embercom/objects/inbox/conversation';
import type ConversationTableEntry from 'embercom/objects/inbox/conversation-table-entry';
import type LatestConversationSummary from 'embercom/objects/inbox/latest-conversation-summary';
import { type LinkedTicket } from 'embercom/objects/inbox/renderable-data';
import { createSafeAdminLink } from 'embercom/utils/admin-link';

interface Args {
  part: RenderablePart;
  conversation: Conversation | LatestConversationSummary | ConversationTableEntry | LinkedTicket;
  hideLinks?: boolean;
}

interface Signature {
  Args: Args;
}

export default class TicketTypeChangedByAdminPartComponent extends Component<Signature> {
  @service declare session: Session;
  @service declare intl: IntlService;

  // @ts-ignore helper doesn't seem to be typed correctly
  linkWithTextHelper = LinkWithText.create(this);

  get isConversationRead() {
    return (this.args.conversation as Conversation).isRead;
  }

  get renderableData() {
    return this.args.part.renderableData as TicketTypeChangedByAdmin;
  }

  get teammate() {
    return this.renderableData.adminSummary;
  }

  get previousTicketTypeName() {
    return this.renderableData.previousTicketTypeName;
  }

  get previousTicketTypeEmoji() {
    return this.renderableData.previousTicketTypeEmoji;
  }

  get newTicketTypeName() {
    return this.renderableData.newTicketTypeName;
  }

  get newTicketTypeEmoji() {
    return this.renderableData.newTicketTypeEmoji;
  }

  get previousTicketTypeDisplay() {
    let emoji = this.previousTicketTypeEmoji;
    return emoji ? `${emoji} ${this.previousTicketTypeName}` : this.previousTicketTypeName;
  }

  get newTicketTypeDisplay() {
    let emoji = this.newTicketTypeEmoji;
    return emoji ? `${emoji} ${this.newTicketTypeName}` : this.newTicketTypeName;
  }

  get didIAuthorTheEvent() {
    return this.teammate?.id === this.session.teammate.id;
  }

  get translationKey() {
    return this.didIAuthorTheEvent
      ? 'inbox.ticket-type-changed.you-changed-ticket-type'
      : 'inbox.ticket-type-changed.admin-changed-ticket-type';
  }

  get translationData() {
    let data: {
      previousType: string;
      newType: string;
      who?: string;
    } = {
      previousType: this.previousTicketTypeDisplay,
      newType: this.newTicketTypeDisplay,
    };

    if (!this.didIAuthorTheEvent) {
      if (this.args.hideLinks) {
        data.who = this.teammate?.name;
      } else {
        data.who = createSafeAdminLink(this.teammate, this.linkWithTextHelper);
      }
    }

    return data;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::EventParts::TicketTypeChangedByAdmin': typeof TicketTypeChangedByAdminPartComponent;
    'inbox2/conversation-stream/event-parts/ticket-type-changed-by-admin': typeof TicketTypeChangedByAdminPartComponent;
  }
}
